// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@awc/App/Widgets/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@awc/App/Widgets/GoogleAnalyticsConnector/Utilities/DataLayer";
import { addToEndOfChain, ApiHandler } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { chain as AddCartLinesChain } from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import { chain as AddLinesToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import { chain as AddToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { chain as AddWishListToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import { chain as ReorderOrderDetailsChain } from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { chain as ReorderOrderHistoryChain } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { chain as ReorderOrderStatusChain } from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import { chain as AddCartLineCollectionFromProductsChain } from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import { chain as AddLinesToCartRequisitionsChain } from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import { chain as PlaceOrderSavedOrderDetailsChain } from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";

type CartLineData = Pick<
    CartLineModel,
    "id" | "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties" | "brand"
>;

type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;

export const XngGaAddProductToCart: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    if (state.xGoogleAnalytics.addToCartEvent) {
        const dataLayer = getDataLayer();
        const session = getSession(state);

        if (!dataLayer) {
            return;
        }

        let cartLinesToAdd: CartLineData[] = [];
        if (props.apiResult?.cartLines || props.apiParameter?.cartLineCollection?.cartLines) {
            // XNG-Changes: this if condition is for Order History Reorder functionality
            if ((props.apiResult?.cartLines as any)?.cartLines) {
                cartLinesToAdd = (props.apiResult?.cartLines as any)?.cartLines || [];
            } else {
                cartLinesToAdd = props.apiResult?.cartLines || props.apiParameter.cartLineCollection?.cartLines || [];
            }
        } else {
            cartLinesToAdd = [
                {
                    shortDescription: props.apiResult.shortDescription || "",
                    erpNumber: props.apiResult.erpNumber || "",
                    pricing: props.apiResult.pricing || null,
                    qtyOrdered: props.apiResult.qtyOrdered || null,
                    properties: props.apiResult.properties,
                    brand: props.apiResult.brand,
                    id: props.apiResult.id,
                },
            ];
        }

        const totalPrice =
            cartLinesToAdd.length > 1
                ? cartLinesToAdd.reduce((total, line) => {
                    if (line.pricing && line.pricing.extendedUnitNetPrice) {
                        return total + line.pricing.extendedUnitNetPrice;
                    }
                    return total;
                }, 0)
                : cartLinesToAdd[0].pricing?.unitNetPrice;

        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.addToCartEventName,
            ecommerce: {
                add: {
                    products: cartLinesToAdd.map((cartLine, idx) => ({
                        id: cartLine.id,
                        name: cartLine.shortDescription,
                        brand: state.xGoogleAnalytics.includeProductBrand
                            ? (cartLine as CartLineModel).brand
                            : undefined,
                        category: state.xGoogleAnalytics.includeProductCategory
                            ? cartLine.properties?.["l1Category"] || ""
                            : undefined,
                        price: cartLine.pricing?.unitListPriceDisplay,
                        quantity: cartLine.qtyOrdered,
                    })),
                }

            },
        };

        dataLayer.push(googleAnalyticsData);
    }
};

const _GaAddProductToCart = XngGaAddProductToCart as any;
addToEndOfChain(AddToCartChain, _GaAddProductToCart);
addToEndOfChain(AddWishListToCartChain, _GaAddProductToCart);
addToEndOfChain(AddCartLinesChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderDetailsChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderHistoryChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderStatusChain, _GaAddProductToCart);
addToEndOfChain(AddCartLineCollectionFromProductsChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartRequisitionsChain, _GaAddProductToCart);
addToEndOfChain(PlaceOrderSavedOrderDetailsChain, _GaAddProductToCart);
